import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth, ROLE_LABELS } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../../ui/alert-dialog";
import { toast } from "sonner";
import { Plus, RefreshCw, KeyRound, ShieldOff, ShieldCheck, Trash2, Lock, Shield } from "lucide-react";
import RolesTab from "./RolesTab";
import EmployeePermissionsDialog from "./EmployeePermissionsDialog";

// Sprint Enterprise RBAC (Feb 2026): rolopties worden nu dynamisch geladen
// uit /api/admin/rbac/roles zodat custom rollen automatisch verschijnen.

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "medewerker", label: "Medewerker" },
  { value: "content_manager", label: "Content Manager" },
  { value: "webshop_manager", label: "Webshop Manager" },
];

export default function MedewerkersSection() {
  const { user, hasPermission, isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [dynamicRoles, setDynamicRoles] = useState([]);       // rbac_v2 rollen
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "medewerker" });
  const [resetFor, setResetFor] = useState(null);
  const [resetPass, setResetPass] = useState("");
  const [permsForUser, setPermsForUser] = useState(null);     // user_id whose perms dialog open

  const canCreate = hasPermission("users:create");
  const canEdit = hasPermission("users:edit");
  const canDisable = hasPermission("users:disable");
  const canDelete = isOwner();
  const canManagePerms = isOwner() || hasPermission("permissions:manage");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rUsers, rRoles] = await Promise.all([
        adminApi.get("/admin/users"),
        adminApi.get("/admin/rbac/roles").catch(() => ({ data: { roles: [] } })),
      ]);
      setItems(rUsers.data);
      setDynamicRoles(rRoles.data?.roles || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreate = async () => {
    if (!form.name || !form.email || !form.password || !form.role) {
      toast.error("Alle velden zijn verplicht.");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Wachtwoord moet minimaal 8 tekens zijn.");
      return;
    }
    try {
      await adminApi.post("/admin/users", form);
      toast.success("Medewerker aangemaakt");
      setCreating(false);
      setForm({ name: "", email: "", password: "", role: "medewerker" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onChangeRole = async (id, role) => {
    try {
      await adminApi.put(`/admin/users/${id}`, { role });
      toast.success("Rol gewijzigd");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onToggleDisable = async (u) => {
    try {
      await adminApi.put(`/admin/users/${u.id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? "Account geactiveerd" : "Account gedeactiveerd");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDelete = async (id) => {
    try {
      await adminApi.delete(`/admin/users/${id}`);
      toast.success("Medewerker verwijderd");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onResetPassword = async () => {
    if (!resetPass || resetPass.length < 8) {
      toast.error("Wachtwoord moet minimaal 8 tekens zijn.");
      return;
    }
    try {
      await adminApi.post(`/admin/users/${resetFor.id}/reset-password`, { new_password: resetPass });
      toast.success("Wachtwoord opnieuw ingesteld");
      setResetFor(null);
      setResetPass("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Rol-opties komen nu dynamisch uit rbac_v2 (systeem + custom)
  const roleOptions = (dynamicRoles.length > 0
    ? dynamicRoles.filter((r) => !r.disabled).map((r) => ({ value: r.key, label: r.name }))
    : ROLE_OPTIONS
  );

  return (
    <div data-testid="admin-section-medewerkers">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-chivo font-black text-3xl text-[#0A192F] tracking-tight">Medewerkers & Rollen</h1>
          <p className="text-sm text-gray-600 mt-1">Beheer accounts, rollen en toegangsrechten</p>
        </div>
      </div>

      <Tabs defaultValue="employees" className="mt-6" data-testid="medewerkers-tabs">
        <TabsList className="bg-slate-50 border border-slate-200 rounded-full p-1 h-auto">
          <TabsTrigger value="employees" className="rounded-full px-4 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"
                       data-testid="medewerkers-tab-employees">
            Medewerkers
          </TabsTrigger>
          <TabsTrigger value="roles" className="rounded-full px-4 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"
                       data-testid="medewerkers-tab-roles">
            Rollen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="mt-4">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" size="sm" className="rounded-none" data-testid="medewerkers-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Vernieuwen
          </Button>
          {canCreate && (
            <Dialog open={creating} onOpenChange={setCreating}>
              <DialogTrigger asChild>
                <Button
                  data-testid="medewerker-new-button"
                  className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"
                >
                  <Plus className="w-4 h-4 mr-1" /> Nieuwe medewerker
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Nieuwe medewerker</DialogTitle>
                  <DialogDescription>Maak een nieuw account aan en wijs een rol toe; medewerker ontvangt het tijdelijk wachtwoord.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-bold">Naam</Label>
                    <Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-bold">E-mailadres</Label>
                    <Input data-testid="user-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-bold">Tijdelijk wachtwoord (min. 8)</Label>
                    <Input data-testid="user-form-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-bold">Rol</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="user-form-role" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {roleOptions.map((r) => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="user-form-save" onClick={onCreate} className="w-full rounded-none bg-[#050505] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs">
                    Aanmaken
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="mt-6 bg-white border border-gray-200 overflow-x-auto" data-testid="users-table-wrap">
        {loading ? (
          <div className="p-6 text-gray-500">Laden...</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] border-b border-gray-200 text-left">
              <tr>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Naam</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">E-mail</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Rol</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Status</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">2FA</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const isOwnerRow = u.role === "owner";
                const isSelf = u.id === user?.id;
                const lockedForViewer = isOwnerRow && !isOwner();
                return (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                    <td className="p-3 font-medium">
                      {u.name}
                      {isSelf && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#0055FF]">(jij)</span>}
                    </td>
                    <td className="p-3 text-xs">{u.email}</td>
                    <td className="p-3">
                      {isOwnerRow ? (
                        <span className="inline-flex items-center gap-1 text-xs font-chivo font-bold uppercase tracking-wide text-[#0055FF]">
                          <Lock className="w-3 h-3" /> Owner
                        </span>
                      ) : canEdit && !lockedForViewer ? (
                        <Select value={u.role} onValueChange={(v) => onChangeRole(u.id, v)}>
                          <SelectTrigger className="w-44 rounded-none h-8 text-xs" data-testid={`user-role-${u.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {roleOptions.map((r) => (
                              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs">{ROLE_LABELS[u.role] || u.role}</span>
                      )}
                    </td>
                    <td className="p-3">
                      {u.disabled ? (
                        <span className="inline-block bg-red-100 text-red-700 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold">Gedeactiveerd</span>
                      ) : (
                        <span className="inline-block bg-green-100 text-green-700 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold">Actief</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-gray-500">
                      {u.two_factor_enabled ? "Aan" : "Uit (placeholder)"}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        {canManagePerms && !isOwnerRow && (
                          <button
                            onClick={() => setPermsForUser(u.id)}
                            className="p-2 text-gray-400 hover:text-[#0055FF]"
                            title="Rechten aanpassen"
                            data-testid={`user-perms-${u.id}`}
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                        )}
                        {canEdit && !lockedForViewer && !isSelf && (
                          <button
                            onClick={() => setResetFor(u)}
                            className="p-2 text-gray-400 hover:text-[#0055FF]"
                            title="Wachtwoord resetten"
                            data-testid={`user-reset-${u.id}`}
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                        )}
                        {canDisable && !lockedForViewer && !isSelf && (
                          <button
                            onClick={() => onToggleDisable(u)}
                            className={`p-2 ${u.disabled ? "text-green-600 hover:text-green-700" : "text-gray-400 hover:text-orange-500"}`}
                            title={u.disabled ? "Activeren" : "Deactiveren"}
                            data-testid={`user-toggle-disable-${u.id}`}
                          >
                            {u.disabled ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
                          </button>
                        )}
                        {canDelete && !isOwnerRow && !isSelf && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="p-2 text-gray-400 hover:text-red-500" title="Verwijderen" data-testid={`user-delete-${u.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Medewerker verwijderen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Dit verwijdert het account van {u.email} permanent. Deze actie kan niet ongedaan worden gemaakt.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                <AlertDialogAction onClick={() => onDelete(u.id)} data-testid={`user-confirm-delete-${u.id}`}>
                                  Verwijderen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Reset password dialog */}
      <Dialog open={!!resetFor} onOpenChange={(o) => { if (!o) { setResetFor(null); setResetPass(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Wachtwoord resetten</DialogTitle>
            <DialogDescription>Genereer een nieuw tijdelijk wachtwoord voor deze medewerker.</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-gray-600">
            Voor: <strong>{resetFor?.email}</strong>
          </div>
          <Input
            type="text"
            placeholder="Nieuw wachtwoord (min. 8)"
            value={resetPass}
            onChange={(e) => setResetPass(e.target.value)}
            className="rounded-none"
            data-testid="reset-password-input"
          />
          <Button onClick={onResetPassword} data-testid="reset-password-confirm" className="rounded-none bg-[#050505] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs">
            Wachtwoord opnieuw instellen
          </Button>
        </DialogContent>
      </Dialog>

        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <RolesTab />
        </TabsContent>
      </Tabs>

      {permsForUser && (
        <EmployeePermissionsDialog
          userId={permsForUser}
          onClose={() => setPermsForUser(null)}
        />
      )}
    </div>
  );
}
